import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  User,
  Message,
  PlayerState,
  Room,
  RoomState,
  Product,
  GeneralRoomParams,
} from "module/types";
import SocketService from "network/SocketService";
import SocketProtocol from "network/SocketProtocol";
import WaitingRoomView from "./WaitingRoomView";
import UserSession from "network/UserSession";
import JorneyToHomeView from "./j2h/JorneyToHomeView";
import WhosTheBotView from "./wtb/WhosTheBotView";
import ServerStatusUI from "components/common/ServerStatusUI";
import { logAnalyticsEvent } from "api/firebaseManager";
import RoleDistributorView from "./roleDistributor/RoleDistributorView";
import DealerJackView from "./dealerjack/DealerJackView";
import BattleJackView from "./battlejack/BattleJackView";
import QuizView from "./quiz/QuizView";
import { useTranslation } from "react-i18next";
import { Conversation, generateConversationKey, PUBLIC_CHAT_KEY } from "module/chatTypes";
import {
  loadSyncInfo,
  updateLastReadIndex,
  getLastReadIndex,
} from "module/chatSync";

export interface InGameViewProps {
  product: Product;
  room: Room;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  onReturnToWaitingRoom: () => void;
}

const GamePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const state = location.state as GeneralRoomParams;
  const [userList, setUserList] = useState<User[]>([]);
  const [aiCount, setAiCount] = useState(0);
  const [allPlayerReady, setAllPlayerReady] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [roomState, setRoomState] = useState(RoomState.WAITING);
  const [gameSetting, setGameSetting] = useState<{}>({});

  // Private chat state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeTab, setActiveTab] = useState<"public" | "private">("public");
  const [activeChatKey, setActiveChatKey] = useState<string>(PUBLIC_CHAT_KEY);
  const [showTargetSelector, setShowTargetSelector] = useState(false);
  const [latestServerIndex, setLatestServerIndex] = useState<number>(-1);

  // Keep ref to latest userList for socket handlers
  const userListRef = useRef<User[]>([]);

  // Helper function to update conversation list
  const updateConversation = (conversationKey: string, lastMessage: Message) => {
    setConversations((prev) => {
      const existing = prev.find((c) => c.id === conversationKey);

      if (existing) {
        // Update existing conversation
        return prev.map((c) =>
          c.id === conversationKey
            ? {
                ...c,
                lastMessage,
                unreadCount:
                  activeChatKey === conversationKey ? 0 : c.unreadCount + 1,
              }
            : c
        );
      } else {
        // Create new conversation
        const participantIds = conversationKey
          .replace("conv_", "")
          .split("_")
          .map(Number);

        const participantNames = participantIds
          .map((id) => userListRef.current.find((u) => u.id === id)?.nickName || `User ${id}`)
          .filter((name) => name !== UserSession.getInstance().getUserNickName());

        const newConversation: Conversation = {
          id: conversationKey,
          participantIds,
          participantNames,
          lastMessage,
          unreadCount: activeChatKey === conversationKey ? 0 : 1,
          createdAt: Date.now(),
        };

        return [...prev, newConversation];
      }
    });
  };

  // 재연결을 위한 게임 상태 저장
  useEffect(() => {
    if (state) {
      // localStorage에 현재 게임 정보 저장
      const gameInfo = {
        recreationId: state.product.id,
        roomName: state.room.roomName,
        isInGame: roomState === RoomState.INGAME,
      };
      localStorage.setItem("currentGameInfo", JSON.stringify(gameInfo));
    }
  }, [state, roomState]);

  // after endGame
  const backToWaitingRoom = () => {
    // 서버에 상태 전달
    SocketService.getInstance().sendChangeState(
      state.product.id,
      state.room.roomName,
      PlayerState.WAITING
    );

    // 게임 상태 false로 변경 → WaitingRoomView로 전환 (Local)
    // Server Room State는 모든 플레이어가 InGame에서 벗어났을 때
    // TODO: 일정 시간 이후 자동으로 InGame 끝내기
    setRoomState(RoomState.WAITING);
  };

  // update ai count
  useEffect(() => {
    setAiCount(
      userList.filter((user) => {
        return user.isAI;
      }).length
    );
  }, [userList]);

  // Keep userListRef in sync with userList state
  useEffect(() => {
    userListRef.current = userList;
  }, [userList]);

  useEffect(() => {
    const socket = SocketService.getInstance();

    socket.on(
      SocketProtocol.USER_LIST,
      (newList: User[], isPlayersAllWaiting: boolean) => {
        UserSession.getInstance().setHost(
          newList.find((user) => user.id === UserSession.getInstance().user.id)
            ?.isHost ?? false
        );
        setUserList(newList);
        setAllPlayerReady(isPlayersAllWaiting);
      }
    );

    socket.on(SocketProtocol.START_GAME, () => {
      console.log("START GAME RECEIVED");
      setRoomState(RoomState.INGAME);
      setMessages([]);
      socket.sendChangeState(
        state.product.id,
        state.room.roomName,
        PlayerState.INGAME
      );
    });

    SocketService.getInstance().on(
      SocketProtocol.ADD_AI_PLAYER,
      (aiName: string) => {
        // call once
        SocketService.getInstance().emit(
          SocketProtocol.CHAT,
          state.product.id,
          state.room.roomName,
          {
            user: aiName,
            content: " added.",
            type: "join",
            smallChatKey: "",
          }
        );
      }
    );

    // Chat listener - persists throughout the game session
    socket.on(SocketProtocol.CHAT, (message: Message) => {
      setMessages((prev) => {
        // Note: smallChatKey filtering is handled in Chatbox for game-specific chats
        // This ensures all messages are captured at the GamePage level

        // Skip join/leave messages if no user list context
        if (message.type === "join" || message.type === "leave") {
          // These messages will be transformed in the Chatbox component
        }

        return [...prev, message];
      });

      // Update conversation list for private messages
      if (privateChatEnabled && message.smallChatKey && message.smallChatKey.startsWith("conv_")) {
        updateConversation(message.smallChatKey, message);

        // Update sync info if message has index
        if (message.index !== undefined) {
          updateLastReadIndex(
            state.product.id,
            state.room.roomName,
            message.smallChatKey,
            message.index
          );
        }
      }
    });

    const initialJoinMessage = {
      user: UserSession.getInstance().getUserNickName(),
      userId: UserSession.getInstance().getUserId(),
      content: t("joinMessage"),
      type: "join",
      smallChatKey: "",
    };

    // Send initial join message
    socket.emit(
      SocketProtocol.CHAT,
      state.product.id,
      state.room.roomName,
      initialJoinMessage
    );

    // socket.on(SocketProtocol.ROOM_STATE, (roomState) =>
    // setRoomState((roomState as RoomState) ?? RoomState.WAITING)
    // );

    // Leave room
    // socket.on(SocketProtocol.LEAVE_ROOM, (recreationId, roomName) => {
    //   let sessionId = parseSessionId(socket);
    //   handleUserLeaveRoom(recreationId, roomName, sessionId, socket);
    // });

    // socket.on(SocketProtocol.LEAVE_ROOM, (leftUserId: string) => {
    //   // setUserList((prev) => prev.filter((u) => u.id !== leftUserId));
    //   setMessages((prev) => [
    //     ...prev,
    //     {
    //       user: "System",
    //       content: `${leftUserId} has left the game.`,
    //       type: "leave",
    //     },
    //   ]);
    // });

    socket.on(SocketProtocol.RECONNECT_TO_GAME, (reconnectedUser: User) => {
      setUserList((prev) => {
        const exists = prev.find((u) => u.id === reconnectedUser.id);
        return exists ? prev : [...prev, reconnectedUser];
      });
      setMessages((prev) => [
        ...prev,
        {
          user: "System",
          userId: 0,
          content: `${reconnectedUser.nickName} reconnected.`,
          type: "join",
          smallChatKey: "",
        },
      ]);
    });

    // Private chat event handlers (only if feature is enabled)
    const privateChatEnabled = state.product.attributes.features?.privateChat;

    if (privateChatEnabled) {
      // Receive latest index when joining room
      socket.on(
        SocketProtocol.LATEST_INDEX,
        (response: { latestIndex: number }) => {
          setLatestServerIndex(response.latestIndex);

          // Load sync info and request history for existing conversations
          const syncInfo = loadSyncInfo(state.product.id, state.room.roomName);

          syncInfo.forEach((info) => {
            if (info.lastReadIndex < response.latestIndex) {
              // Request missing messages
              socket.emit(SocketProtocol.REQUEST_CHAT_HISTORY, {
                productId: state.product.id,
                roomName: state.room.roomName,
                conversationKey: info.conversationKey,
                fromIndex: info.lastReadIndex + 1,
                limit: 50,
              });
            }
          });
        }
      );

      // Receive chat history
      socket.on(
        SocketProtocol.CHAT_HISTORY,
        (response: {
          conversationKey: string;
          messages: Message[];
          latestIndex: number;
          hasMore: boolean;
        }) => {
          // Add received messages to state
          setMessages((prev) => {
            const newMessages = response.messages.filter(
              (msg) => !prev.find((p) => p.index === msg.index)
            );
            return [...prev, ...newMessages];
          });

          // Update conversation list
          if (response.messages.length > 0) {
            const lastMessage = response.messages[response.messages.length - 1];
            updateConversation(response.conversationKey, lastMessage);
          }

          // Update sync info
          if (response.latestIndex > -1) {
            updateLastReadIndex(
              state.product.id,
              state.room.roomName,
              response.conversationKey,
              response.latestIndex
            );
          }
        }
      );
    }

    return () => {
      socket.off(SocketProtocol.USER_LIST);
      socket.off(SocketProtocol.ADD_AI_PLAYER);
      socket.off(SocketProtocol.START_GAME);
      socket.off(SocketProtocol.ROOM_STATE);
      socket.off(SocketProtocol.LEAVE_ROOM);
      socket.off(SocketProtocol.RECONNECT_TO_GAME);
      socket.off(SocketProtocol.CHAT);

      if (privateChatEnabled) {
        socket.off(SocketProtocol.LATEST_INDEX);
        socket.off(SocketProtocol.CHAT_HISTORY);
      }

      socket.emit(
        SocketProtocol.LEAVE_ROOM,
        state.product.id,
        state.room.roomName
      );
    };
  }, [state.product.id, state.room.roomName, t]);

  const handleLeaveRoom = (navigatePop: boolean) => {
    SocketService.getInstance().sendChangeState(
      state.product.id,
      state.room.roomName,
      PlayerState.DISCONNECTED
    );
    SocketService.getInstance().emit(
      SocketProtocol.LEAVE_ROOM,
      state.product.id,
      state.room.roomName
    );
    if (navigatePop) {
      navigate("/room", { replace: true, state });
    }
  };

  const handleStartGame = (setting: object) => {
    if (!allPlayerReady) {
      //TODO: show not ready popup
      return;
    }

    // 게임 시작 이벤트 로깅
    logAnalyticsEvent("game_start", {
      recreation_id: state.product.id,
      game_title: state.product.title,
      player_count: userList.length,
      ai_count: aiCount,
    });

    SocketService.getInstance().emit(
      SocketProtocol.START_GAME,
      state.product.id,
      state.room.roomName,
      setting
    );
    setGameSetting(setting);
  };

  const handleReconnect = () => {
    SocketService.getInstance().emit(
      SocketProtocol.RECONNECT_TO_GAME,
      state.product.id,
      state.room.roomName,
      UserSession.getInstance().user.id
    );
  };

  // Private chat handlers
  const handleTabChange = (tab: "public" | "private") => {
    setActiveTab(tab);
    if (tab === "public") {
      setActiveChatKey(PUBLIC_CHAT_KEY);
    }
  };

  const handleNewConversation = () => {
    setShowTargetSelector(true);
  };

  const handleConversationSelect = (conversationId: string) => {
    setActiveChatKey(conversationId);

    // Mark conversation as read
    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
    );
  };

  const handleTargetConfirm = (selectedIds: number[]) => {
    const allParticipantIds = [...selectedIds, UserSession.getInstance().getUserId()];
    const conversationKey = generateConversationKey(allParticipantIds);

    // Check if conversation already exists
    const existing = conversations.find((c) => c.id === conversationKey);

    if (!existing) {
      // Create new conversation
      const participantNames = selectedIds
        .map((id) => userList.find((u) => u.id === id)?.nickName || `User ${id}`);

      const newConversation: Conversation = {
        id: conversationKey,
        participantIds: allParticipantIds,
        participantNames,
        unreadCount: 0,
        createdAt: Date.now(),
      };

      setConversations((prev) => [...prev, newConversation]);
    }

    // Switch to private tab and select this conversation
    setActiveTab("private");
    setActiveChatKey(conversationKey);
    setShowTargetSelector(false);
  };

  const handleTargetCancel = () => {
    setShowTargetSelector(false);
  };

  const renderInGameView = () => {
    switch (state.product.id) {
      case 0:
        return (
          <JorneyToHomeView
            product={state.product}
            room={state.room}
            messages={messages}
            setMessages={setMessages}
            onReturnToWaitingRoom={backToWaitingRoom}
          />
        );
      case 1:
        return (
          <WhosTheBotView
            product={state.product}
            room={state.room}
            messages={messages}
            setMessages={setMessages}
            onReturnToWaitingRoom={backToWaitingRoom}
          />
        );
      case 2:
        return (
          <RoleDistributorView
            product={state.product}
            room={state.room}
            messages={messages}
            setMessages={setMessages}
            onReturnToWaitingRoom={backToWaitingRoom}
          />
        );
      case 6:
        return (
          <DealerJackView
            product={state.product}
            room={state.room}
            messages={messages}
            setMessages={setMessages}
            onReturnToWaitingRoom={backToWaitingRoom}
          ></DealerJackView>
        );
      case 7:
        return (
          <QuizView
            product={state.product}
            room={state.room}
            messages={messages}
            setMessages={setMessages}
            onReturnToWaitingRoom={backToWaitingRoom}
          />
        );
      default:
        return <div>Unsupported game type</div>;
    }
  };

  return (
    <>
      <ServerStatusUI />
      {roomState === RoomState.WAITING ? (
        <WaitingRoomView
          roomParams={state}
          userList={userList}
          aiCount={aiCount}
          messages={messages}
          roomState={roomState}
          allPlayerReady={allPlayerReady}
          setMessages={setMessages}
          onLeaveRoom={handleLeaveRoom}
          onStartGame={handleStartGame}
          onReconnect={handleReconnect}
          // Private chat props (only passed if feature is enabled)
          {...(state.product.attributes.features?.privateChat && {
            conversations,
            activeTab,
            activeChatKey,
            showTargetSelector,
            onTabChange: handleTabChange,
            onNewConversation: handleNewConversation,
            onConversationSelect: handleConversationSelect,
            onTargetConfirm: handleTargetConfirm,
            onTargetCancel: handleTargetCancel,
          })}
        />
      ) : (
        renderInGameView()
      )}
    </>
  );
};

export default GamePage;
